import type { CalendarScheduleItem, EventCalendarDates } from './AddToCalendar.types';
import { CALENDAR_ICS_PATH } from './AddToCalendar.constants';

const ISO_DAY = /^\d{4}-\d{2}-\d{2}/;

const toIsoTimestamp = (timestamp?: Maybe<string>): string | undefined => {
  if (!timestamp) {
    return undefined;
  }
  const date = new Date(timestamp);
  return isNaN(date.getTime()) ? undefined : date.toISOString();
};

/**
 * Converts an event date to a UTC ISO string normalized to midnight UTC, for use as an all-day entry.
 *
 * Calendar libraries read all-day events with `.getUTCDate()`, so a local-midnight value would land on
 * the previous or next day depending on the guest's timezone.
 *
 * Two input shapes reach this, and only one of them may be parsed:
 *  - ISO-8601 (`2027-06-12T16:00:00-04:00`) — the leading `YYYY-MM-DD` already *is* the event-local
 *    day, so it is sliced. Parsing would re-read that day in the guest's timezone and shift it, and a
 *    date-only ISO string parses as UTC midnight rather than local midnight.
 *  - a human date (`June 12, 2027`) — no timezone to lose; parses as local midnight, so local parts
 *    are the intended day.
 */
export const toAllDayCalendarDate = (value?: Maybe<string>): string | undefined => {
  if (!value) {
    return undefined;
  }

  const isoDay = ISO_DAY.exec(value)?.[0];
  if (isoDay) {
    return `${isoDay}T00:00:00.000Z`;
  }

  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return undefined;
  }
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
};

type CalendarIcsUrlArgs = Readonly<{
  eventId?: string;
  scheduleItemId?: string;
  provider: 'apple' | 'google' | 'outlook' | 'other';
  title?: string;
  start: Date;
  /** Exclusive next day for an all-day entry, the item's own end otherwise. */
  end: Date;
  allDay: boolean;
  location?: string;
  description?: string;
}>;

/**
 * Percent-encoding is what actually has to fit, and it is not one byte per character: a CJK
 * character costs nine. A description that is comfortable in English is nine times over budget in
 * Japanese, and the request then dies at the edge — nginx allows roughly 8KB for a request line
 * and Node's default `max-http-header-size` is 16KB — as a 414/431 the guest sees as a broken tab.
 * So the entry degrades rather than the button breaking: the fields are budgeted by encoded size,
 * longest and least essential first.
 */
const TITLE_BUDGET = 400;
const LOCATION_BUDGET = 1200;
const DESCRIPTION_BUDGET = 4000;

const encodedCostCache = new Map<string, number>();

/**
 * What a character costs in the serialized query, measured with the serializer that actually
 * writes it.
 *
 * `URLSearchParams` emits application/x-www-form-urlencoded, which escapes more than
 * `encodeURIComponent` does: `'`, `!`, `(`, `)` and `~` each cost three characters rather than
 * one, while a space costs one (`+`) rather than three. Apostrophes are everywhere in event
 * titles, so budgeting with the wrong encoder undercounts them threefold — 4,000 apostrophes
 * measure as 4,000 and arrive as 12,000. Distinct characters are few, so the cache keeps this
 * exact without paying for the allocation per character.
 */
const encodedCost = (character: string): number => {
  const cached = encodedCostCache.get(character);
  if (cached !== undefined) {
    return cached;
  }

  const cost = new URLSearchParams({ v: character }).toString().length - 'v='.length;
  encodedCostCache.set(character, cost);
  return cost;
};

/**
 * Longest prefix of `value` whose serialized form fits `maxEncoded` characters. Iterating code
 * points keeps a multi-byte character whole — half a character would encode to a broken sequence.
 */
const truncateToEncodedLength = (value: string, maxEncoded: number): string => {
  let encodedLength = 0;
  let result = '';

  for (const character of value) {
    const cost = encodedCost(character);
    if (encodedLength + cost > maxEncoded) {
      break;
    }
    result += character;
    encodedLength += cost;
  }

  return result;
};

/**
 * Builds the link the Apple option opens.
 *
 * Apple needs a file where Google and Outlook take a hosted URL, and a file the browser builds
 * for itself never arrives on iOS: WebKit ignores the `download` attribute that `datebook`'s
 * `ICalendar.download()` relies on, so the tap does nothing. Asking the server for the same
 * bytes turns all three providers into the same gesture — open a link.
 *
 * All-day dates are anchored to midnight UTC by `toAllDayCalendarDate`, and `toISOString` keeps
 * them there, so the server reads back the day the couple actually chose.
 */
export const buildCalendarIcsUrl = ({ eventId, scheduleItemId, provider, title, start, end, allDay, location, description }: CalendarIcsUrlArgs): string => {
  const query = new URLSearchParams({
    provider,
    title: truncateToEncodedLength(title ?? '', TITLE_BUDGET),
    start: start.toISOString(),
    end: end.toISOString(),
  });
  if (eventId) query.set('eventId', eventId);
  if (allDay) query.set('allDay', 'true');
  if (scheduleItemId) query.set('scheduleItemId', scheduleItemId);
  if (location) query.set('location', truncateToEncodedLength(location, LOCATION_BUDGET));
  if (description) query.set('description', truncateToEncodedLength(description, DESCRIPTION_BUDGET));

  return `${CALENDAR_ICS_PATH}?${query.toString()}`;
};

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The window an "Add to Calendar" entry covers.
 *
 * A start-only item gets 15 minutes — the smallest slot a calendar draws, which reads as "starts
 * here" without inventing an arbitrary block. An all-day item has no end at all, and that absence
 * is what marks it: datebook infers all-day from a missing end, and the `.ics` route is told so
 * explicitly. All-day entries end on the exclusive next day, per RFC 5545.
 */
export const resolveCalendarDates = ({ start, end, allDay }: Readonly<{ start: string; end?: string; allDay?: boolean }>) => {
  const dateStart = new Date(start);
  // An unparseable end is treated as no end at all. Ecard callers derive one from a free-text card
  // date, and carrying `Invalid Date` through would throw at `toISOString()` — inside a click
  // handler, which is the silent "nothing happens" this route exists to remove.
  const parsedEnd = end ? new Date(end) : undefined;
  const dateEnd = parsedEnd && !Number.isNaN(parsedEnd.getTime()) ? parsedEnd : allDay ? undefined : new Date(dateStart.getTime() + FIFTEEN_MINUTES_MS);

  return {
    dateStart,
    dateEnd,
    icsEnd: dateEnd ?? new Date(dateStart.getTime() + ONE_DAY_MS),
    isAllDay: !dateEnd,
  };
};

/**
 * Resolves the dates an "Add to Calendar" entry should carry for the event as a whole.
 *
 * The main schedule item is the only time the couple can actually edit, so it wins and is passed
 * through untouched — the same values the Schedule page hands to its own button, so both land on the
 * same calendar entry. The event date only carries a meaningful day (its time is a creation-time
 * default with no editor behind it), so it degrades to an all-day entry rather than publishing an
 * hour nobody chose.
 */
export const getEventCalendarDates = (args: Readonly<{ mainScheduleItem?: Maybe<CalendarScheduleItem>; eventDate?: Maybe<string> }>): EventCalendarDates | undefined => {
  const { mainScheduleItem, eventDate } = args;

  const scheduleStart = toIsoTimestamp(mainScheduleItem?.startTime?.timestamp);
  if (scheduleStart) {
    return {
      start: scheduleStart,
      end: toIsoTimestamp(mainScheduleItem?.endTime?.timestamp),
      allDay: false,
    };
  }

  const allDayStart = toAllDayCalendarDate(eventDate);
  return allDayStart ? { start: allDayStart, allDay: true } : undefined;
};
